<template>
  <tx-autocomplete
    ref="refAutocomplete" :model-value="currentComponentModelValue"
    :clearable="clearable"
    :disabled="disabled"
    :errors="errors"
    :fetch-suggestions="searchModelNumber"
    :label="label"
    :placeholder="placeholder"
    :required="required"
    display-prop="ModelNumber"
    value-prop="ModelNumber"
    @update:model-value="onModelValueUpdated"
  />
</template>

<script setup lang="ts">
import { ref, watchEffect } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import type { ErrorObject } from '@vuelidate/core'
import TxAutocomplete from './TxAutocomplete.vue'
import utils from '@/services/utils'
import type { SellerModelsBase } from '@/api/t1/model/sellerModel'
import { appConstants } from '@/models/constants'
import { useUserStore } from '@/store/userData'
import type { ArticleFilterModel } from '@/api/t1/model/articleModel'
import { FilterCriteriaMode } from '@/models/filterCriteria'
import { getArticleFilterValues } from '@/api/t1/article'

interface IProps {
  clearable?: boolean
  disabled?: boolean
  errors?: ErrorObject[]
  label?: string
  modelValue?: any
  placeholder?: string
  required?: boolean
}
const props = withDefaults(defineProps<IProps>(), {
  clearable: true,
  disabled: false,
  errors: () => [] as ErrorObject[],
  label: '',
  modelValue: null,
  placeholder: '',
  required: false,
})

const emit = defineEmits<{
  (e: 'change', modelValue: any): void
  (e: 'update:modelValue', modelValue: SellerModelsBase | null): void
}>()

const userStore = useUserStore()
const refAutocomplete = ref<InstanceType<typeof TxAutocomplete>>()
const currentComponentModelValue: any = ref(props.modelValue)
const sellerModelsIndexes = ref<Record<string, SellerModelsBase>>({})

watchEffect(() => {
  if (props.modelValue != null && typeof props.modelValue === 'object') {
    currentComponentModelValue.value = props.modelValue.ModelNumber
  }
  else {
    currentComponentModelValue.value = props.modelValue
  }
})

const debouncedUpdateModelFunction = useDebounceFn((modelValue) => {
  const val = sellerModelsIndexes.value[modelValue]
  currentComponentModelValue.value = val
  emit('change', val)
  emit('update:modelValue', val)
}, appConstants.debounce.input)

function onModelValueUpdated(value) {
  debouncedUpdateModelFunction(value)
}

async function searchModelNumber(query: string, callback) {
  sellerModelsIndexes.value = {}
  if (userStore.activeCatalog) {
    const filters: ArticleFilterModel[] = []
    const linkedCatalogCodes = Object.keys(userStore.linkedCatalogDetails)
    if (linkedCatalogCodes.length) {
      const seasonFilter: ArticleFilterModel = { attribute: '_Seasons', mode: FilterCriteriaMode.multiString, value: [], exclude: false }
      linkedCatalogCodes.forEach((linkedCatalogCode) => {
        seasonFilter.value.push(Number(linkedCatalogCode))
      })
      filters.push(seasonFilter)
    }
    if (utils.isDefined(query)) {
      filters.push({ attribute: 'ModelNumber', mode: FilterCriteriaMode.startWithString, value: query.trim(), exclude: false })
    }
    getArticleFilterValues(userStore.activeCatalog.CatalogCode, { attributes: ['ModelId', 'ModelNumber', 'ModelName'], filters, sortCriteria: { attribute: 'ModelNumber', isAsc: true } }, 100, true)
      .then((data) => {
        callback(data)
        const modelIndexes: Record<string, SellerModelsBase> = {}
        data.forEach((item) => {
          modelIndexes[item.ModelNumber] = item as SellerModelsBase
        })
        sellerModelsIndexes.value = modelIndexes
      })
  }
  else {
    callback([])
  }
}

defineExpose({
  focus,
})
</script>
